import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { useMutation } from '@/utils/api';

interface HealthReportResponse {
  status?: string;
  message?: string;
  grade?: string;
  score?: number;
}

interface UseGenerateHealthReportReturn {
  generate: () => void;
  isGenerating: boolean;
}

/**
 * Hook to trigger on-demand health report generation.
 *
 * POST /report/health — synchronous Lambda invocation (≤29s). Backend
 * renders the PDF, uploads to S3, and emails the HTML report with a
 * "Download Full Report (PDF)" CTA linking to a 7-day presigned URL.
 *
 * Frontend simply reports email delivery. A 202 response means the
 * handler exceeded its time budget and did NOT send the email; surface
 * that honestly as a warning so the user knows to retry.
 */
export function useGenerateHealthReport(): UseGenerateHealthReportReturn {
  const axios = useAxios();

  const mutation = useMutation<void, Error, void>({
    defaultErrorMessage: 'Failed to generate health report',
    mutationFn: async () => {
      const toastId = toast.loading('Generating health report...', {
        duration: Infinity,
      });

      try {
        const response =
          await axios.post<HealthReportResponse>('/report/health');

        toast.dismiss(toastId);

        if (response.data?.status === 'accepted') {
          toast.warning(
            response.data.message ?? 'Report generation deferred.',
            { duration: 10000 }
          );
          return;
        }

        toast.success('Risk Score Report has been emailed.');
      } catch (error) {
        toast.dismiss(toastId);

        if (
          error instanceof Error &&
          error.message.toLowerCase().includes('already in progress')
        ) {
          toast.error('A report is already being generated. Please wait.');
        } else {
          toast.error(
            error instanceof Error
              ? error.message
              : 'Failed to generate health report',
            { duration: 8000 }
          );
        }

        throw error;
      }
    },
  });

  return {
    generate: mutation.mutate,
    isGenerating: mutation.isPending,
  };
}
